import { Compass, Gauge, Lock, Sparkles } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { InfoHint } from '../InfoHint';
import type {
  CoupleOperatingSystemScore,
  RelationshipAdaptiveChallenge,
  RelationshipConfidenceBand,
  RelationshipQuickWin,
} from '../../lib/relationship-intelligence-engine';

export function StabilityCockpitCard({
  osScore,
  confidenceBand,
  quickWins,
  adaptiveChallenges,
  isPremium,
  onUpgrade,
  onTrack,
}: {
  osScore: CoupleOperatingSystemScore;
  confidenceBand: RelationshipConfidenceBand;
  quickWins: RelationshipQuickWin[];
  adaptiveChallenges: RelationshipAdaptiveChallenge[];
  isPremium: boolean;
  onUpgrade: () => void;
  onTrack?: (event: string, properties?: Record<string, unknown>) => void;
}) {
  const proChallenges = adaptiveChallenges.slice(0, 2);

  return (
    <Card className="p-4 border-violet-200/80 bg-violet-50/70 dark:border-violet-900/50 dark:bg-violet-950/25">
      <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
        <span className="inline-flex items-center gap-1.5">
          Relationship stability cockpit
          <InfoHint
            title="Relationship stability cockpit"
            description="A live action cockpit showing confidence, stability factors, and adaptive next-step paths."
          />
        </span>
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 dark:border-violet-800/50 dark:bg-neutral-900/70">
          <p className="text-[11px] uppercase text-neutral-500">Overall</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {osScore.overall} · {osScore.label}
          </p>
        </div>
        <div className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 dark:border-violet-800/50 dark:bg-neutral-900/70">
          <p className="text-[11px] uppercase text-neutral-500">Stability</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{osScore.stability}</p>
        </div>
        <div className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 dark:border-violet-800/50 dark:bg-neutral-900/70">
          <p className="text-[11px] uppercase text-neutral-500">Repair speed</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{osScore.repairVelocity}</p>
        </div>
        <div className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 dark:border-violet-800/50 dark:bg-neutral-900/70">
          <p className="text-[11px] uppercase text-neutral-500">Backslide risk</p>
          <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{osScore.relapseRisk}</p>
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-indigo-200/70 bg-white/80 px-3 py-2 text-xs dark:border-indigo-900/50 dark:bg-neutral-900/70">
        <p className="inline-flex items-center gap-1 text-indigo-700 dark:text-indigo-300">
          <Gauge className="h-3.5 w-3.5" />
          {confidenceBand.label} ({confidenceBand.score}%)
        </p>
        <p className="mt-1 text-neutral-600 dark:text-neutral-300">{confidenceBand.rationale}</p>
      </div>

      <div className="mt-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-neutral-500">Immediate quick wins</p>
        <div className="mt-1.5 space-y-1.5">
          {quickWins.slice(0, 2).map((quickWin) => (
            <div key={quickWin.id} className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 text-xs dark:border-violet-800/40 dark:bg-neutral-900/70">
              <p className="font-semibold text-neutral-900 dark:text-neutral-100">{quickWin.title}</p>
              <p className="mt-0.5 text-neutral-600 dark:text-neutral-300">
                {quickWin.instruction} · {quickWin.timing} · expected shift {quickWin.expectedDelta.toFixed(1)}
              </p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-3 rounded-lg border border-violet-200/70 bg-gradient-to-r from-violet-100/80 to-pink-100/80 px-3 py-2 dark:border-violet-800/40 dark:from-violet-950/30 dark:to-pink-950/20">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">Adaptive challenge lab</p>
        {isPremium ? (
          <div className="mt-1.5 space-y-1.5">
            {proChallenges.map((challenge) => (
              <div key={challenge.id} className="rounded-lg border border-violet-200/70 bg-white/85 px-2.5 py-2 text-xs dark:border-violet-800/40 dark:bg-neutral-900/70">
                <p className="font-semibold text-neutral-900 dark:text-neutral-100">{challenge.title}</p>
                <p className="mt-0.5 text-neutral-600 dark:text-neutral-300">
                  {challenge.description}
                </p>
                <p className="mt-0.5 text-neutral-600 dark:text-neutral-300">
                  Target {challenge.targetMetric.replace('_', ' ')} · {challenge.estimatedMinutes} min · impact {challenge.expectedDelta.toFixed(1)} · confidence {Math.round(challenge.confidence * 100)}%
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-violet-300/60 bg-white/85 px-2.5 py-2 dark:border-violet-700 dark:bg-neutral-900/70">
            <p className="text-xs text-neutral-700 dark:text-neutral-200">
              Pro unlocks autonomous challenge sequencing, confidence recalibration, and adaptive strategy autoplay.
            </p>
            <Button
              size="sm"
              onClick={() => {
                onTrack?.('ri_upgrade_prompt_clicked', { surface: 'adaptive_challenge_lab' });
                onUpgrade();
              }}
            >
              <Lock className="mr-1.5 h-3.5 w-3.5" />
              Unlock Pro
            </Button>
          </div>
        )}
      </div>
      <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-neutral-600 dark:text-neutral-300">
        <Compass className="h-3.5 w-3.5" />
        Week-over-week change: {osScore.weekDelta > 0 ? `-${osScore.weekDelta}` : `${osScore.weekDelta}`} score points.
      </p>
      <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-600 dark:text-neutral-300">
        <Sparkles className="h-3.5 w-3.5" />
        Advanced mode blends forecast confidence with adaptive challenge outcomes.
      </p>
    </Card>
  );
}
